// app/src/lib/cashflow/ownerDraw.js — owner's draw tracking for cashflow.
//
// An "owner's draw" is money the owner takes out of the business account for
// themselves. It is NOT a business expense — it's profit moved to the owner's
// pocket — so cashflow must exclude it from expenses and track it on its own.
//
// Draws are identified by user-defined keywords (a name, the last-4 of a
// destination account, "owner draw", "distribution", etc.) matched against the
// bank transaction's merchant/description. Keywords live on the local
// userProfile row (carried by backups + the team shared snapshot).
//
// An owner W2 paycheck IS a draw here (owner-confirmed): money to the owner is
// money to the owner, however payroll labels it — add the paycheck's bank
// descriptor to the draw keywords and it lands in the same bucket.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { is401kContribution } from './payroll.js';
import { isRevenueClawback, matchDepositChannel, depositRulesWithOwnerKeywords } from '../revenue/depositRules.js';
// Safe direction: settledSales imports neither ownerDraw nor sales. (sales.js
// imports ownerDraw, so ownerDraw must never import sales.js — that is a cycle.)
import { settledNetByMonth } from '../revenue/settledSales.js';
import { creditAccountIds, withoutCreditTxs, isCardPayment } from './accounts.js';
import { markBooksDirty } from '../backup/sharedSnapshot.js';

const MAX_KEYWORDS = 25;

async function getKeywordList(field) {
  const c = await currentClaims();
  if (!c) return [];
  const p = await db.userProfile.get(c.sub);
  return p && Array.isArray(p[field]) ? p[field] : [];
}

async function setKeywordList(field, list) {
  const c = await currentClaims();
  if (!c) throw new Error('owner-draw: not signed in');
  const clean = [...new Set((list || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean))].slice(0, MAX_KEYWORDS);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  await db.userProfile.put({ ...existing, userId: c.sub, [field]: clean });
  return clean;
}

export const getKeywords = () => getKeywordList('ownerDrawKeywords');
export const setKeywords = (list) => setKeywordList('ownerDrawKeywords', list);

// The operator's own 401(k) transaction labels (their bank's descriptor for the
// retirement-plan transfer), layered on top of the built-in provider hints in
// payroll.is401kContribution so a 401(k) is counted as money to the owner, not a
// business expense.
export const get401kKeywords = () => getKeywordList('retirement401kKeywords');
export const set401kKeywords = (list) => setKeywordList('retirement401kKeywords', list);

/**
 * True if a transaction matches any owner-draw keyword.
 * Match the MERCHANT/counterparty name, not the full bank description: ACH
 * descriptors embed the account holder's name (e.g. "ind name: Tariga Tahong")
 * inside unrelated card-autopay / insurance payments, so matching the whole
 * description falsely flagged those as draws. Only fall back to the description
 * when no merchant name is present.
 */
export function isOwnerDraw(tx, keywords) {
  if (!keywords || !keywords.length) return false;
  // A credit-card payment (Plaid PFC 'LOAN_PAYMENTS') is NEVER an owner draw —
  // it pays down a card. Banks embed the account holder's name in the ACH
  // descriptor ("IND NAME: <owner>"), which collides with a name-based draw
  // keyword the operator added to catch Zelle-to-self. Real draws are transfers
  // (TRANSFER_OUT), so excluding card payments up front removes the false
  // positives (e.g. Capital One / Amex autopay) without touching real draws.
  if (tx && tx.category === 'LOAN_PAYMENTS') return false;
  const name = (tx.merchantName || '').toLowerCase();
  const hay = name || (tx.description || '').toLowerCase();
  return keywords.some((k) => k && hay.includes(String(k).toLowerCase()));
}

const ymOf = (iso) => (iso || '').slice(0, 7);

// Financial month for DEPOSIT attribution: money landing on the 1st/2nd is the
// previous month's T+2 settlement tail, so a deposit "month" runs from the 3rd
// of a calendar month through the 2nd of the NEXT. Fixed cutoff: a 1st/2nd that
// lands on a weekend/holiday is intentionally NOT shifted. Costs do NOT use
// this — see the per-flow rule in monthlySummary (owner-confirmed 2026-07).
export const finMonthOf = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return (iso || '').slice(0, 7);
  let yy = y, mm = m;
  if (d <= 2) { mm -= 1; if (mm < 1) { mm = 12; yy -= 1; } }
  return `${yy}-${String(mm).padStart(2, '0')}`;
};
const ymLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'short' }); };

// ── Prior-year distributions (owner-confirmed final model) ──────────────────
// The double-counting boundary is the CALENDAR YEAR: an earning must never be
// pocketed twice across years, while within a year the owner draws and
// retains freely. Two detectors, combined:
//
//  1. TX-LEVEL (primary): owners pay themselves in STANDING amounts (biweekly
//     Zelle, fixed checks). A draw whose amount (±1%) never repeats across two
//     distinct months of the full history is a ONE-OFF — a distribution of
//     prior-year retained earnings. Once detected it DISAPPEARS from the
//     pocket columns entirely (it was already in the pocket the year it was
//     earned); only a muted "not in Total" sub-line remains. This keeps
//     retained POSITIVE in months that genuinely earned more than the regular
//     pay (e.g. a January with last year's lump check in it).
//
//  2. YEAR BACKSTOP: if the REMAINING (standing) draws still cumulatively
//     exceed the calendar year's earnings — e.g. a distribution staggered in
//     identical chunks, which defeats the amount test — the year waterfall
//     (splitYearDraws) flags the excess, attributed chronologically. Within
//     the year, a month merely drawing ahead of its own earnings stays
//     visible as negative retained; only a true year-level overdraw is
//     prior-year money.
//
// Invariant: pocket = draw + retirement + retained = operating net, always —
// distributions never touch operating net, so removing them changes only the
// decomposition, never the totals.

/** Cluster tolerance: two draw amounts are "the same" within 1% (min $1). */
function sameAmount(a, b) {
  return Math.abs(a - b) <= Math.max(100, Math.min(a, b) * 0.01);
}

/**
 * The set of plaidTxIds among `drawTxs` that are ONE-OFFS (non-repeating
 * amounts → prior-year distributions). Sort by amount, chain-cluster adjacent
 * amounts within tolerance, then mark every cluster whose transactions span
 * fewer than 2 distinct calendar months.
 */
export function oneOffDrawTxIds(drawTxs) {
  const sorted = [...drawTxs].sort((a, b) => (a.amountCents || 0) - (b.amountCents || 0));
  const out = new Set();
  let cluster = [];
  const flush = () => {
    if (!cluster.length) return;
    const months = new Set(cluster.map((t) => ymOf(t.date)));
    if (months.size < 2) for (const t of cluster) out.add(t.plaidTxId);
    cluster = [];
  };
  for (const t of sorted) {
    if (cluster.length && !sameAmount(cluster[cluster.length - 1].amountCents || 0, t.amountCents || 0)) flush();
    cluster.push(t);
  }
  flush();
  return out;
}

/**
 * The year waterfall. `monthRows` = ONE calendar year's months in order, each
 * `{ earnedCents, drawsCents }`. Returns the per-month split, same order.
 * Exported for reuse/tests.
 */
export function splitYearDraws(monthRows) {
  let cumEarn = 0, cumDraws = 0;
  const cumEquity = monthRows.map((r) => {
    cumEarn += r.earnedCents || 0;
    cumDraws += r.drawsCents || 0;
    return Math.max(0, cumDraws - Math.max(0, cumEarn));
  });
  const finalE = cumEquity.length ? cumEquity[cumEquity.length - 1] : 0;
  let attributed = 0;
  return monthRows.map((r, i) => {
    const equityDrawCents = Math.max(0, Math.min(cumEquity[i], finalE) - attributed);
    attributed += equityDrawCents;
    const drawCents = (r.drawsCents || 0) - equityDrawCents;
    return { drawCents, equityDrawCents, retainedCents: (r.earnedCents || 0) - drawCents };
  });
}

// ── Prior-year reserve (current model) ──────────────────────────────────────
// The bank balance on the prior 12/31 IS last year's accumulated retained
// earnings — already counted as income in the years it was made. Early-year
// ONE-OFF draws (lumps, not the repeating W2/scheduled paycheck) come out of
// THAT pile, not out of new earnings; counting them as new income double-counts.
// We seed a reserve at that 12/31 balance and deplete it as one-off draws land,
// so the report can show "$X year-end balance → $Y remaining" instead.

/**
 * Balance as of 12/31 of `year` (cents), or null if not derivable.
 *   1. Exact: the running-balance column on the latest transaction at/before the
 *      boundary (persisted by the CSV importer when the statement carried one).
 *   2. Roll back from the current account balance using signed amounts (Plaid:
 *      +amount = outflow). Exact as long as every transaction since the boundary
 *      is on file — balance(boundary) = current + Σ(amount after boundary).
 * @param {Array<{date:string, amountCents:number, balanceCents?:number}>} txs
 * @param {number} year
 * @param {number|null} currentBalanceCents
 */
export function deriveYearEndBalance(txs, year, currentBalanceCents) {
  const boundary = `${year}-12-31`;
  const withBal = (txs || [])
    .filter((t) => t && t.balanceCents != null && (t.date || '') <= boundary)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  if (withBal.length) return withBal[withBal.length - 1].balanceCents;
  if (currentBalanceCents == null) return null;
  const afterSum = (txs || [])
    .filter((t) => t && (t.date || '') > boundary)
    .reduce((s, t) => s + (t.amountCents || 0), 0);
  return currentBalanceCents + afterSum;
}

/**
 * Charge ONE calendar year's draws against its prior-year reserve. `monthRows` is
 * that year's months in order, each { earnedCents, regularDrawsCents,
 * oneOffDrawsCents }. Repeatable (regular) draws are funded by current earnings;
 * one-off lumps deplete the reserve first, then spill into current earnings once
 * it's exhausted. Returns the per-month split, same order.
 *   drawCents                — draws funded by THIS year's earnings (regular + spillover)
 *   reserveDistributionCents — one-off draws funded by the prior-year reserve (NOT new pocket)
 *   reserveRemainingCents    — reserve left after this month
 *   retainedCents            — earned − drawCents (profit kept in the business)
 */
export function applyReserveDrawdown(monthRows, reserveStartCents) {
  // CUMULATIVE: draws are funded FIRST by current-year earnings; only the amount
  // by which cumulative draws exceed cumulative earnings dips into the prior-year
  // reserve. A running HIGH-WATER mark makes that dip permanent (a later
  // high-earning month doesn't un-draw the reserve), capped at the reserve size.
  // So an early-year lump (before earnings accrue) draws the reserve, while
  // late-year draws covered by the year's accumulated earnings do NOT — they're
  // current-year. UNKNOWN reserve (null) ⇒ uncapped: anything beyond cumulative
  // earnings is treated as prior-year, with no running "remaining" figure.
  const known = reserveStartCents != null;
  const cap = known ? Math.max(0, Number(reserveStartCents) || 0) : Infinity;
  let cumEarned = 0, cumDraws = 0, hwUsed = 0;
  return (monthRows || []).map((r) => {
    const earned = r.earnedCents || 0;
    const draws = (r.regularDrawsCents || 0) + (r.oneOffDrawsCents || 0);
    cumEarned += earned;
    cumDraws += draws;
    const over = Math.max(0, cumDraws - cumEarned);     // drawn beyond current-year earnings
    const newHw = Math.min(cap, Math.max(hwUsed, over)); // monotonic, capped at the reserve
    const reserveDistribution = newHw - hwUsed;          // this month's reserve draw (≥0)
    hwUsed = newHw;
    const drawCents = draws - reserveDistribution;       // remainder funded by current earnings
    return {
      drawCents,
      reserveDistributionCents: reserveDistribution,
      reserveRemainingCents: known ? (cap - hwUsed) : null,
      retainedCents: earned - drawCents,
    };
  });
}

/**
 * RECONCILED drawdown: the operator has CONFIRMED which specific draws were
 * prior-year distributions (via the reconcile modal), so no guessing. For each
 * month the reserve distribution is exactly the marked draws (capped cumulatively
 * at the reserve); everything else is current-year. `monthRows`:
 * { earnedCents, totalDrawsCents, markedDrawsCents }. Same return shape as
 * applyReserveDrawdown.
 */
export function applyReconciledDrawdown(monthRows, reserveStartCents) {
  const known = reserveStartCents != null;
  const cap = known ? Math.max(0, Number(reserveStartCents) || 0) : Infinity;
  let cumMarked = 0;
  return (monthRows || []).map((r) => {
    const earned = r.earnedCents || 0;
    const totalDraws = r.totalDrawsCents || 0;
    const marked = r.markedDrawsCents || 0;
    const reserveDistribution = Math.min(marked, Math.max(0, cap - cumMarked));
    cumMarked += reserveDistribution;
    const drawCents = totalDraws - reserveDistribution;
    return {
      drawCents,
      reserveDistributionCents: reserveDistribution,
      reserveRemainingCents: known ? (cap - cumMarked) : null,
      retainedCents: earned - drawCents,
    };
  });
}

/**
 * Sum of depository (cash) account balances — the current bank cash position.
 * Returns null when NO account carries a balance (so callers can tell "we don't
 * know the balance" apart from a genuine $0), which keeps the reserve UNKNOWN.
 */
const MANUAL_BALANCE_ITEMS = new Set(['upload', 'csv-import']);

async function currentCashCents(userId) {
  const accts = await db.plaidAccounts.where('userId').equals(userId).toArray();
  const dep = accts.filter((a) => a.type === 'depository' && a.currentBalanceCents != null);
  if (!dep.length) return null;
  // Prefer live Plaid balances when they're stored.
  const live = dep.filter((a) => !MANUAL_BALANCE_ITEMS.has(a.plaidItemId));
  if (live.length) return live.reduce((s, a) => s + (a.currentBalanceCents || 0), 0);
  // Only a manual (CSV) balance exists — it's an as-of snapshot that goes stale
  // as new (Plaid) transactions post. Roll it FORWARD to today through the
  // depository transactions so callers (the reserve rollback) get the CURRENT
  // balance, not the import-date one.
  const base = dep.reduce((s, a) => s + (a.currentBalanceCents || 0), 0);
  const anchor = dep.slice().sort((a, b) => (a.balanceAsOf || '').localeCompare(b.balanceAsOf || '')).pop();
  if (!anchor || !anchor.balanceAsOf) return base;
  const credit = new Set(accts.filter((a) => a.type === 'credit').map((a) => a.plaidAccountId));
  const txsAll = await db.plaidTransactions.where('userId').equals(userId).toArray();
  const afterAnchor = txsAll
    .filter((t) => !credit.has(t.plaidAccountId) && (t.date || '') > anchor.balanceAsOf)
    .reduce((s, t) => s + (t.amountCents || 0), 0); // >0 outflow lowered balance since the anchor
  return base - afterAnchor;
}

/** User-confirmed year-end-balance overrides: { [year]: cents }. */
export async function getYearEndOverrides() {
  const c = await currentClaims();
  if (!c) return {};
  const p = await db.userProfile.get(c.sub);
  return (p && p.yearEndBalances && typeof p.yearEndBalances === 'object') ? p.yearEndBalances : {};
}

/** Confirm / override the 12/31 balance for `year` (pass null to clear). */
export async function setYearEndBalance(year, cents) {
  const c = await currentClaims();
  if (!c) throw new Error('owner-draw: not signed in');
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  const map = { ...(existing.yearEndBalances || {}) };
  if (cents == null) delete map[String(year)];
  else map[String(year)] = Math.round(Number(cents) || 0);
  await db.userProfile.put({ ...existing, userId: c.sub, yearEndBalances: map });
  return map;
}

// Per-year reconciliation: the operator's confirmed set of owner-draw plaidTxIds
// that were prior-year distributions. Keyed by the draw YEAR. When a year has an
// entry, monthlySummary uses these marks instead of the cumulative auto-guess.
export async function getReserveReconcile() {
  const c = await currentClaims();
  if (!c) return {};
  const p = await db.userProfile.get(c.sub);
  return (p && p.reserveReconcile && typeof p.reserveReconcile === 'object') ? p.reserveReconcile : {};
}

/** Save the confirmed prior-year draw ids for `year` (pass null to clear → back to auto). */
export async function setReserveReconcile(year, txIds) {
  const c = await currentClaims();
  if (!c) throw new Error('owner-draw: not signed in');
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  const map = { ...(existing.reserveReconcile || {}) };
  if (txIds == null) delete map[String(year)];
  else map[String(year)] = [...new Set((txIds || []).map(String).filter(Boolean))];
  await db.userProfile.put({ ...existing, userId: c.sub, reserveReconcile: map });
  return map;
}

/**
 * Per-month owner-draw / contribution summary over the trailing `months` months.
 * The SAME keywords match both directions (same accounts): an outflow is a draw
 * (money to the owner), an inflow is a contribution (owner capital in). Neither
 * is operations: draws are NOT an expense, contributions are NOT revenue.
 * For each month:
 *   ownerDrawCents         — draws covered by THIS month's earnings (the
 *                            waterfall above; incl. a W2 paycheck whose
 *                            descriptor is in the draw keywords)
 *   equityDrawCents        — draws beyond this month's earnings: a
 *                            distribution of prior retained earnings. Already
 *                            counted in the pocket when earned, so excluded
 *                            from draw/retained/pocket and reported apart.
 *   ownerRetirementCents   — 401(k) contributions (usually the owner's own
 *                            retirement). Money to the owner, NOT a business
 *                            operating expense — so it's excluded from expenses
 *                            and counted on the owner's side, like a draw.
 *   ownerContributionCents — keyword-matched inflows
 *   operatingNetCents      — (revenue inflows) − (business expenses, excl. the
 *                            owner-side outflows above)
 *   retainedCents          — earnings − draw (profit left in the business;
 *                            negative only in a loss month)
 *   pocketCents            — draw + retirement + retained = operating net
 *                            (the by-month table shows this)
 */
export async function monthlySummary({ months = 12, now = new Date() } = {}) {
  const c = await currentClaims();
  if (!c) return { series: [], keywords: [], currentDrawCents: 0, currentContributionCents: 0, currentRetirementCents: 0, totalDrawCents: 0, totalContributionCents: 0, totalRetirementCents: 0, totalEquityDrawCents: 0 };
  const userId = c.sub;
  const keywords = await getKeywords();
  const k401Keywords = await get401kKeywords();
  // Card CHARGES are the spend, counted on the day charged; the bill payment
  // (both sides — see accounts.isCardPayment) is the settlement transfer of
  // spend already counted, so it is skipped in the month BUCKETS below. It is
  // NOT skipped from balance math: the checking debit really moves the bank
  // balance, so the year-end-reserve rollback (deriveYearEndBalance) runs on
  // the full DEPOSITORY ledger including it.
  const rawTxs = await db.plaidTransactions.where('userId').equals(userId).filter((t) => !t.pending).toArray();
  const creditSet = await creditAccountIds(userId);
  const txs = withoutCreditTxs(rawTxs, creditSet);            // bank ledger: draws + balance derivation
  const flowTxs = rawTxs.filter((t) => !isCardPayment(t));    // month buckets: bill payments transparent

  // One-off detection over the FULL history (not just the reported window) so
  // a standing amount keeps its "repeating" status even in a 1-month view.
  const drawTxs = txs.filter((t) => (t.amountCents || 0) > 0
    && !isRevenueClawback(t) && !is401kContribution(t, k401Keywords) && isOwnerDraw(t, keywords));
  const oneOffIds = oneOffDrawTxIds(drawTxs);


  // Draws the operator has CONFIRMED are distributions of the retained pot.
  // Unlike the sets above these are not a guess and are not year-scoped: the
  // pot is all-time, so a retained draw belongs to the pot, not to a year.
  const drawKinds = await getDrawKinds();

  // Anchor on the current CALENDAR month: costs are calendar-dated (per-flow
  // rule below), so a just-started month already carries its 1st-of-month rent
  // and payroll; deposits dated the 1st/2nd still report into the prior month.
  const nowIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const [anchorY, anchorM] = nowIso.slice(0, 7).split('-').map(Number);
  const buckets = {};
  const order = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(anchorY, anchorM - 1 - i, 1);
    order.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  // The year waterfall needs each calendar year COMPLETE from January, even
  // when the reported window starts mid-year — otherwise a January lump's
  // coverage by the year's own earnings can't be computed. Extend the
  // computed months back to Jan of the first window year (reported months
  // stay `order`).
  const extOrder = [];
  {
    let y = Number(order[0].slice(0, 4)), m = 1;
    const lastYm = order[order.length - 1];
    for (;;) {
      const ym = `${y}-${String(m).padStart(2, '0')}`;
      extOrder.push(ym);
      if (ym === lastYm) break;
      m += 1; if (m > 12) { m = 1; y += 1; }
    }
  }
  let depositRules;
  try {
    const prof = await db.userProfile.get(userId);
    depositRules = depositRulesWithOwnerKeywords({
      cashKeywords: prof?.cashDepositKeywords, cardKeywords: prof?.cardDepositKeywords,
    });
  } catch { depositRules = depositRulesWithOwnerKeywords(); }

  for (const ym of extOrder) {
    buckets[ym] = { ym, label: ymLabel(ym), regularDrawsCents: 0, distributionCents: 0, retainedDrawCents: 0, retainedPriorDrawCents: 0, ownerRetirementCents: 0, ownerContributionCents: 0, totalFlowCents: 0, salesInflowCents: 0 };
  }
  // Per-flow month attribution (owner-confirmed 2026-07): a bank DEPOSIT dated
  // the 1st/2nd is the prior month's T+2 settlement tail → financial month
  // (3rd→2nd); every other flow — rent, payroll, supplier ACH, draws, and card
  // CHARGES (spend on the day charged) — counts in its calendar month. Card
  // bill payments were dropped above (settlement transfers, not spend), so
  // retained is attributed earnings, not an exact bank-balance change.
  for (const t of flowTxs) {
    const amt = t.amountCents || 0;
    const isCardTx = creditSet.has(t.plaidAccountId);
    const b = buckets[!isCardTx && amt < 0 ? finMonthOf(t.date) : ymOf(t.date)];
    if (!b) continue; // Plaid: >0 outflow, <0 inflow
    // Net flow of every transaction: bank deposits/expenses plus card charges
    // (spend when charged, refunds netting against it), so operating net /
    // retained are derived from it — not reconstructed from salesEntries −
    // expenses, which drops any deposit that didn't match a sales channel.
    b.totalFlowCents += amt;
    // A card transaction is pure spend (charge) or its refund — never a draw,
    // 401(k), clawback, or owner contribution (those are bank transfers).
    if (isCardTx) continue;
    if (amt > 0) {
      // Platform clawbacks are already netted into channel revenue by
      // syncFromDeposits (and flow in via the settled inflows below) — counting
      // them as an expense too would double the hit.
      if (isRevenueClawback(t)) continue;
      // An operator-CONFIRMED retained draw is checked FIRST, before the
      // keyword test — this is the bug that made a $100k distribution read as
      // a $100k business expense. The confirmation used to live INSIDE the
      // isOwnerDraw() branch, so a lump the keywords did not happen to
      // recognise could never be reclassified: it sank operating net, and
      // recentDraws() filtered it out of the classify sheet too, so there was
      // no way to correct it. A human saying "this is a distribution" outranks
      // any keyword guess.
      //
      // It is a distribution of the retained pot (lib/cashflow/retainedBalance.js):
      // not an expense, not new income, and NOT a draw against this month's
      // earnings — so it stays out of the buckets that feed the waterfall and
      // is subtracted from the pot instead.
      const kind = drawKinds.get(t.plaidTxId);
      if (kind === 'retained' || kind === 'retainedPrior') {
        b.retainedDrawCents += amt;
        if (kind === 'retainedPrior') b.retainedPriorDrawCents += amt;
      }
      // 401(k) (owner's retirement) before draw keywords before generic expense.
      else if (is401kContribution(t, k401Keywords)) b.ownerRetirementCents += amt;
      else if (isOwnerDraw(t, keywords)) {
        if (oneOffIds.has(t.plaidTxId)) b.distributionCents += amt;
        else b.regularDrawsCents += amt;
      }
      // else: an operating expense — already captured in totalFlowCents.
    } else {
      // Owner contributions (capital IN) are excluded from earnings (it's the
      // owner funding the business, not revenue). Every other inflow — including
      // deposits that didn't match a sales channel — counts, because retained
      // is the real bank-balance change.
      if (isOwnerDraw(t, keywords)) { b.ownerContributionCents += -amt; continue; }
      // A deposit that IS sales revenue gets tracked separately so it can be
      // swapped for the sale-dated figure below. Deposits that match no sales
      // channel (interest, vendor refunds) are left in totalFlowCents on their
      // own date — they are real money in the pocket and nothing else reports them.
      if (matchDepositChannel(t, depositRules)) b.salesInflowCents += -amt;
    }
  }

  // ── Revenue basis: sale-dated, not deposit-dated ──────────────────────────
  // Sales revenue is recognised on the day it was EARNED (syncFromDeposits
  // attributes every payout back to its sale days), so the pocket must be built
  // on the same base as the Sales figure — otherwise "in your pocket" can exceed
  // the month's sales, which is impossible when pocket = draw + 401k + retained.
  //
  // The raw flows bucketed sales deposits by finMonthOf, which only pulls a
  // deposit back a month when it lands on the 1st or 2nd. A Monday-the-3rd card
  // deposit is mostly the PREVIOUS month's Thu/Fri/Sat, and that money was being
  // counted in the wrong month here while the Sales tile had it right.
  //
  // So: take the recognised sales inflows back OUT of the flow total and put the
  // settled, sale-dated sales in their place. Non-sales inflows and every outflow
  // are untouched, so retained still tracks real bank movement.
  //
  // GUARDED: only re-date when a sale-dated ledger actually exists for the
  // window. A book that has deposits but no salesEntries (sync never ran, or it
  // failed) would otherwise have its revenue swapped for zero and report the
  // owner's whole income as a loss. In that case the raw deposit-dated basis
  // stands — the old, cruder answer, but never a catastrophic one.
  try {
    const settled = await settledNetByMonth(extOrder);
    const totalSettled = extOrder.reduce((n, ym) => n + (settled[ym] || 0), 0);
    if (totalSettled > 0) {
      for (const ym of extOrder) {
        const b = buckets[ym];
        if (!b) continue;
        b.totalFlowCents += b.salesInflowCents - (settled[ym] || 0);
      }
    }
  } catch { /* settlement unavailable — leave the raw deposit-dated basis */ }

  // Reserve drawdown per COMPLETE calendar year in extOrder. A year's reserve is
  // seeded by the prior 12/31 balance (the operator's confirmed override, else
  // auto-derived). One-off lumps deplete it; regular (repeating) draws are funded
  // by current earnings. The detected one-offs are `distributionCents`.
  const cashCents = await currentCashCents(userId);
  const overrides = await getYearEndOverrides();
  const split = {};
  const yebByYear = {}; // year(YYYY) → { cents, derivedCents, confirmed, priorYear }
  const byYear = new Map();
  for (const ym of extOrder) {
    const y = ym.slice(0, 4);
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y).push(ym);
  }
  for (const [y, yms] of byYear) {
    const priorYear = Number(y) - 1;
    const derivedCents = deriveYearEndBalance(txs, priorYear, cashCents);
    const override = overrides[String(priorYear)];
    // null reserveStart ⇒ UNKNOWN (applyReserveDrawdown falls back to the old
    // one-offs-disappear behavior). A confirmed override always wins.
    const reserveStart = override != null ? override : derivedCents;
    yebByYear[y] = { cents: reserveStart, derivedCents, confirmed: override != null, priorYear };
    const rows = yms.map((ym) => {
      const b = buckets[ym];
      // Balance-anchored: netFlow = −totalFlow = the bank-balance change. Adding
      // back the draws (owner takeout) and removing owner capital-in yields
      // earnings-after-retirement, i.e. (deposits − expenses − 401k). Retained
      // (= earned − regular draw) then equals the actual balance change.
      const earnedCents = -b.totalFlowCents + (b.regularDrawsCents + b.distributionCents + b.retainedDrawCents) - b.ownerContributionCents;
      return { earnedCents, regularDrawsCents: b.regularDrawsCents, oneOffDrawsCents: b.distributionCents };
    });
    // Always the auto model now. The per-year `reserveReconcile` marks used to
    // override it, but their only UI (the reserve-reconcile modal) is gone —
    // leaving the branch would let stored marks drive a year's drawdown forever
    // with no way to inspect, correct or reset them. Saying "this draw was
    // prior-year money" is now done per transaction in the classify sheet,
    // which overrides this model outright rather than tuning it.
    const out = applyReserveDrawdown(rows, reserveStart);
    out.forEach((s, i) => { split[yms[i]] = s; });
  }

  const series = order.map((ym) => {
    const b = buckets[ym];
    // Operating net (pocket) = deposits − expenses = earnings before owner
    // takeout, derived from the real net cash flow (see earnedCents above).
    const operatingNetCents = -b.totalFlowCents + (b.regularDrawsCents + b.distributionCents + b.retainedDrawCents) + b.ownerRetirementCents - b.ownerContributionCents;
    const s = split[ym] || { drawCents: 0, reserveDistributionCents: 0, reserveRemainingCents: 0, retainedCents: 0 };
    const isJan = ym.slice(5, 7) === '01';
    const yeb = isJan ? yebByYear[ym.slice(0, 4)] : null;
    return {
      ym, label: b.label,
      ownerDrawCents: s.drawCents,
      retainedDrawCents: b.retainedDrawCents,        // confirmed distribution of the retained pot
      retainedPriorDrawCents: b.retainedPriorDrawCents, // …of which: against LAST year's closing balance
      equityDrawCents: s.reserveDistributionCents,   // prior-year reserve distribution (not new pocket)
      reserveRemainingCents: s.reserveRemainingCents,
      ownerRetirementCents: b.ownerRetirementCents,
      ownerContributionCents: b.ownerContributionCents,
      operatingNetCents,
      retainedCents: s.retainedCents,
      pocketCents: s.drawCents + b.ownerRetirementCents + s.retainedCents,
      // Year-end-balance boundary marker: shown as a "$X YEB" row before this Jan.
      yebCents: yeb ? yeb.cents : null,
      yebDerivedCents: yeb ? yeb.derivedCents : null,
      yebConfirmed: yeb ? yeb.confirmed : null,
      yebReconciled: yeb ? yeb.reconciled : null,
      yebYear: yeb ? yeb.priorYear : null,
    };
  });
  const last = series[series.length - 1] || {};
  return {
    series,
    keywords,
    currentDrawCents: last.ownerDrawCents || 0,
    currentContributionCents: last.ownerContributionCents || 0,
    currentRetirementCents: last.ownerRetirementCents || 0,
    currentEquityDrawCents: last.equityDrawCents || 0,
    currentReserveRemainingCents: last.reserveRemainingCents || 0,
    totalDrawCents: series.reduce((s, x) => s + x.ownerDrawCents, 0),
    totalContributionCents: series.reduce((s, x) => s + x.ownerContributionCents, 0),
    totalRetirementCents: series.reduce((s, x) => s + x.ownerRetirementCents, 0),
    totalEquityDrawCents: series.reduce((s, x) => s + x.equityDrawCents, 0),
    totalRetainedDrawCents: series.reduce((s, x) => s + x.retainedDrawCents, 0),
    totalRetainedPriorDrawCents: series.reduce((s, x) => s + x.retainedPriorDrawCents, 0),
  };
}

/**
 * Candidate owner-draw transactions for the reserve-reconcile modal for `year`.
 * Returns the prior-year-end balance and the year's draws, each with a `checked`
 * suggestion: if the year is already reconciled, the operator's saved marks;
 * otherwise the auto model's best guess (each month's reserve distribution
 * applied to that month's OLDEST draws first). The modal lets the operator adjust
 * and save via setReserveReconcile.
 */
export async function reconcileCandidates(year) {
  const c = await currentClaims();
  if (!c) return { year, priorYear: year - 1, yebCents: null, txns: [], reconciled: false };
  const userId = c.sub;
  const [keywords, k401, txsAll, creditSet] = await Promise.all([
    getKeywords(), get401kKeywords(),
    db.plaidTransactions.where('userId').equals(userId).filter((t) => !t.pending).toArray(),
    creditAccountIds(userId),
  ]);
  const txs = withoutCreditTxs(txsAll, creditSet); // depository-only (see monthlySummary)
  const yStart = `${year}-01-01`, yEnd = `${year}-12-31`;
  const draws = txs.filter((t) => (t.amountCents || 0) > 0
    && (t.date || '') >= yStart && (t.date || '') <= yEnd
    && !isRevenueClawback(t) && !is401kContribution(t, k401) && isOwnerDraw(t, keywords));
  draws.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const priorYear = year - 1;
  const cashCents = await currentCashCents(userId);
  const overrides = await getYearEndOverrides();
  const yebCents = overrides[String(priorYear)] != null
    ? overrides[String(priorYear)]
    : deriveYearEndBalance(txs, priorYear, cashCents);

  const reconcile = await getReserveReconcile();
  const saved = reconcile[String(year)];
  const suggested = new Set();
  if (!saved) {
    const now = new Date();
    const monthsBack = Math.max(1, (now.getFullYear() - year) * 12 + now.getMonth() + 1);
    const summary = await monthlySummary({ months: monthsBack }).catch(() => ({ series: [] }));
    const distByMonth = {};
    for (const m of (summary.series || [])) {
      if (m.ym.slice(0, 4) === String(year)) distByMonth[m.ym] = m.equityDrawCents || 0;
    }
    const byMonth = {};
    for (const t of draws) (byMonth[(t.date || '').slice(0, 7)] ||= []).push(t);
    for (const [ym, list] of Object.entries(byMonth)) {
      let need = distByMonth[ym] || 0, acc = 0;
      for (const t of list) { if (acc >= need) break; suggested.add(t.plaidTxId); acc += t.amountCents || 0; }
    }
  }
  const txns = draws.map((t) => ({
    plaidTxId: t.plaidTxId, date: t.date, amountCents: t.amountCents || 0,
    merchantName: t.merchantName || t.description || '',
    checked: saved ? saved.includes(t.plaidTxId) : suggested.has(t.plaidTxId),
  }));
  return { year, priorYear, yebCents, reconciled: !!saved, txns };
}

// ── Retained draws (owner-confirmed) ───────────────────────────────────────
// A pure cash distribution that flushes profit the business already earned and
// kept — as opposed to a W2 draw, which is payroll against the month's own
// earnings. The distinction cannot be inferred reliably (that is what the
// amount heuristic above tries to do, and why it has been wrong), so it is the
// operator's to make: this is the set of transactions they have SAID are
// retained draws.
//
// Stored flat and all-time rather than keyed by year: the pot itself is
// all-time, so a draw belongs to the pot, not to a year. See
// lib/cashflow/retainedBalance.js.

// How the owner took the money out. Three kinds, because they are three
// different events in the books:
//   w2            — the standing payroll draw. Paid out of the month's own
//                   earnings, so it reduces retained INCOME. (Payroll is used
//                   for tax timing: withholding lands bi-weekly rather than
//                   quarterly.)
//   retained      — a distribution of profit earned and kept THIS year.
//                   Reduces the pot, not income, and is not an expense.
//   retainedPrior — a distribution against LAST year's closing balance. Also
//                   reduces the pot, but it was already income in the year it
//                   was earned, so it is reported apart for tax.
export const DRAW_KINDS = Object.freeze(['w2', 'retained', 'retainedPrior']);

/** @returns {Promise<Map<string,string>>} plaidTxId → kind. Absent means 'w2'. */
export async function getDrawKinds() {
  const c = await currentClaims();
  if (!c) return new Map();
  const p = await db.userProfile.get(c.sub);
  const m = new Map(Object.entries((p && p.ownerDrawKinds) || {}));
  // Carry forward the earlier boolean list, which only knew "retained".
  for (const id of (Array.isArray(p && p.retainedDrawTxIds) ? p.retainedDrawTxIds : [])) {
    if (!m.has(id)) m.set(id, 'retained');
  }
  return m;
}

/** @returns {Promise<Set<string>>} ids drawn against the pot, either kind. */
export async function getRetainedDrawIds() {
  const m = await getDrawKinds();
  return new Set([...m].filter(([, k]) => k === 'retained' || k === 'retainedPrior').map(([id]) => id));
}

/**
 * Record how a transaction was taken out.
 * @param {string} plaidTxId
 * @param {'w2'|'retained'|'retainedPrior'} kind
 */
export async function setDrawKind(plaidTxId, kind) {
  const c = await currentClaims();
  if (!c) throw new Error('owner-draw: not signed in');
  if (!DRAW_KINDS.includes(kind)) throw new Error(`owner-draw: unknown kind ${kind}`);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  const map = { ...(existing.ownerDrawKinds || {}) };
  // 'w2' is the default, so it is stored as an ABSENCE — otherwise the old
  // boolean list below would keep resurrecting a draw the owner reclassified.
  if (kind === 'w2') delete map[String(plaidTxId)];
  else map[String(plaidTxId)] = kind;
  const legacy = (Array.isArray(existing.retainedDrawTxIds) ? existing.retainedDrawTxIds : [])
    .filter((id) => id !== String(plaidTxId));
  await db.userProfile.put({
    ...existing, userId: c.sub, ownerDrawKinds: map, retainedDrawTxIds: legacy,
  });
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return { kind };
}

/** Back-compat shim for the boolean call site. */
export async function setRetainedDraw(plaidTxId, isRetained) {
  return setDrawKind(plaidTxId, isRetained ? 'retained' : 'w2');
}

/** A payment must clear this to be offered when hunting for a missing draw. */
export const CANDIDATE_FLOOR_CENTS = 250000; // $2,500

// Things that are obviously the business paying someone else. Rent, taxes and
// payroll services are never money to the owner, and listing them alongside
// real draws is noise — the classify sheet showed rent, employment tax and
// sales tax as draws to be sorted, which is worse than not offering candidates
// at all.
const NOT_A_DRAW_TERMS = [
  'rent', 'lease', 'landlord', 'tax', 'irs', 'edd', 'dor', 'treasury', 'revenue',
  'payroll', 'gusto', 'adp', 'paychex', 'quickbooks', 'insur', 'utilit', 'electric',
  'gas', 'water', 'waste', 'internet', 'comcast', 'verizon', 'sysco', 'produce',
  'food', 'supply', 'depot', 'costco', 'amazon', 'square', 'stripe', 'toast',
  'doordash', 'ubereats', 'grubhub', 'loan', 'mortgage', 'interest', 'fee',
];
/** True when a payment is plainly the business paying someone else. */
function looksLikeBusinessPayment(t) {
  const words = `${(t && t.merchantName) || ''} ${(t && t.name) || ''}`
    .toLowerCase().split(/[^a-z0-9&]+/).filter(Boolean);
  return words.some((w) => NOT_A_DRAW_TERMS.some((k) => w.startsWith(k)));
}

/**
 * Owner draws, newest first — the classify sheet's list.
 *
 * By default this is exactly what it says: outflows the draw KEYWORDS
 * recognise, plus anything already classified (so reclassifying something does
 * not make it disappear mid-edit).
 *
 * `candidates: true` additionally offers large unmatched payments, for hunting
 * down a one-off wire the keywords never learned. That is opt-in behind a tap
 * — offered by default it buries the real draws under every rent and tax
 * payment the business makes.
 *
 * Depository only: a card charge is never a draw, and a card BILL payment is a
 * settlement transfer rather than money to the owner.
 *
 * Scoped to the YEAR TO DATE by default: an owner reconciling distributions
 * needs the whole year in front of them, not the most recent handful.
 *
 * @param {{limit?:number, candidates?:boolean, from?:string}|number} [opts]
 */
export async function recentDraws(opts = {}) {
  const o = typeof opts === 'number' ? { limit: opts } : opts;
  const { limit = 500, candidates = false } = o;
  const from = o.from !== undefined ? o.from : `${new Date().getFullYear()}-01-01`;
  const c = await currentClaims();
  if (!c) return [];
  const [keywords, kinds, k401, creditSet] = await Promise.all([
    getKeywords(), getDrawKinds(), get401kKeywords(), creditAccountIds(c.sub),
  ]);
  const all = await db.plaidTransactions.where('userId').equals(c.sub).toArray();
  return all
    .filter((t) => {
      if (creditSet.has(t.plaidAccountId)) return false;
      if (from && (t.date || '') < from) return false;
      if (!((t.amountCents || 0) > 0)) return false;   // Plaid: >0 is an outflow
      if (isCardPayment(t)) return false;
      if (kinds.has(t.plaidTxId)) return true;         // already classified
      // A 401(k) contribution is already recognised on its own and reported as
      // money to the owner; repeating it here would just ask the same question
      // twice.
      if (is401kContribution(t, k401)) return false;
      if (isOwnerDraw(t, keywords)) return true;       // a known draw
      if (!candidates) return false;
      return (t.amountCents || 0) >= CANDIDATE_FLOOR_CENTS && !looksLikeBusinessPayment(t);
    })
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    .slice(0, limit)
    .map((t) => ({
      plaidTxId: t.plaidTxId, date: t.date, amountCents: t.amountCents,
      name: t.name, merchantName: t.merchantName,
      kind: kinds.get(t.plaidTxId) || 'w2',
      matched: isOwnerDraw(t, keywords),
    }));
}

/**
 * Year-to-date owner's pocket: every month of the CURRENT year in the series,
 * summed. "Current" is taken from the last row rather than the wall clock so
 * the figure always describes the data on screen.
 *
 * The 12-month window monthlySummary returns always spans the whole year so
 * far, so this needs no extra query — January is either in the window or is
 * itself the last row.
 *
 * @param {Array<{ ym:string, pocketCents?:number }>} series
 * @returns {{ cents:number, year:string|null, months:number }}
 */
export function ytdPocket(series) {
  const rows = Array.isArray(series) ? series.filter((r) => r && typeof r.ym === 'string') : [];
  if (!rows.length) return { cents: 0, year: null, months: 0 };
  const year = rows[rows.length - 1].ym.slice(0, 4);
  const inYear = rows.filter((r) => r.ym.slice(0, 4) === year);
  return {
    cents: inYear.reduce((sum, r) => sum + (r.pocketCents || 0), 0),
    year,
    months: inYear.length,
  };
}
